import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  X,
  Pencil,
  Save,
  MapPin,
  Clock,
  Route,
  User,
  AlertTriangle,
  Ban,
  Trophy,
  RotateCw,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function ScorePill({ score }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const cls =
    s >= 90 ? "bg-emerald-500 text-white" :
    s >= 75 ? "bg-emerald-100 text-emerald-800" :
    s >= 60 ? "bg-amber-100 text-amber-800" :
              "bg-red-100 text-red-800";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${cls}`}>
      <Trophy className="h-3 w-3" /> {s}
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { label: "Pendiente", cls: "bg-neutral-200 text-neutral-700" },
    accepted: { label: "Aceptada", cls: "bg-emerald-100 text-emerald-800" },
    rejected: { label: "Rechazada", cls: "bg-red-100 text-red-800" },
  };
  const c = map[status] || map.pending;
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${c.cls}`}>{c.label}</span>;
}

function DraftCard({ draft, teachers, onAccept, onReject, onEdit, saving }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    date: draft.date, time: draft.time,
    duration_minutes: draft.duration_minutes,
    teacher_id: draft.teacher_id,
  });

  useEffect(() => { setForm({
    date: draft.date, time: draft.time,
    duration_minutes: draft.duration_minutes, teacher_id: draft.teacher_id,
  }); }, [draft.id]);   

  const submitEdit = async () => {
    await onEdit(draft.id, form);
    setEditing(false);
  };

  const isPending = draft.status === "pending";
  const outOfAvailability = draft.availability_match === false;
  const conflict = draft.incident_conflict === true;

  return (
    <div
      className={
        "rounded-2xl border p-4 space-y-2 " +
        (draft.status === "accepted" ? "border-emerald-300 bg-emerald-50/40" :
         draft.status === "rejected" ? "border-red-200 bg-red-50/40 opacity-75" :
                                       "border-neutral-200 bg-white")
      }
      data-testid={`draft-card-${draft.id}`}
    >
      <div className="flex items-start gap-2 flex-wrap">
        <ScorePill score={draft.score} />
        <StatusPill status={draft.status} />
        {draft.distance_km != null && (
          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-bold px-2 py-0.5">
            <Route className="h-3 w-3" /> {draft.distance_km} km
          </span>
        )}
      </div>

      {editing ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div>
            <label className="block text-[10px] uppercase text-neutral-500 font-bold mb-0.5">Fecha</label>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-xs" data-testid={`draft-edit-date-${draft.id}`} />
          </div>
          <div>
            <label className="block text-[10px] uppercase text-neutral-500 font-bold mb-0.5">Hora</label>
            <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-xs" data-testid={`draft-edit-time-${draft.id}`} />
          </div>
          <div>
            <label className="block text-[10px] uppercase text-neutral-500 font-bold mb-0.5">Duración (min)</label>
            <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-xs" min={30} max={240} step={15} data-testid={`draft-edit-duration-${draft.id}`} />
          </div>
          <div>
            <label className="block text-[10px] uppercase text-neutral-500 font-bold mb-0.5">Profesor</label>
            <select value={form.teacher_id} onChange={(e) => setForm({ ...form, teacher_id: e.target.value })} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-xs" data-testid={`draft-edit-teacher-${draft.id}`}>
              <option value="">— Sin asignar —</option>
              {teachers.filter((t) => t.active !== false).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
          <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-neutral-400" /><b>{draft.date}</b> · {draft.time} <span className="text-neutral-400">({draft.duration_minutes}min)</span></div>
          <div className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-neutral-400" />{draft.teacher_name || <span className="text-neutral-400 italic">Sin profesor</span>}</div>
          {draft.zone && <div className="flex items-center gap-1.5 col-span-2"><MapPin className="h-3.5 w-3.5 text-neutral-400" />{draft.zone}</div>}
        </div>
      )}

      {draft.reason && (
        <div className="text-xs text-neutral-600 leading-snug border-l-2 border-orange-200 pl-2">
          {draft.reason}
        </div>
      )}

      {(outOfAvailability || conflict || (draft.alerts || []).length > 0) && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 space-y-0.5">
          {outOfAvailability && (
            <div className="flex items-center gap-1 text-[11px] text-amber-900" data-testid={`draft-alert-availability-${draft.id}`}>
              <AlertTriangle className="h-3 w-3" /> Horario fuera de disponibilidad declarada
            </div>
          )}
          {conflict && (
            <div className="flex items-center gap-1 text-[11px] text-red-800" data-testid={`draft-alert-conflict-${draft.id}`}>
              <Ban className="h-3 w-3" /> Conflicto con incidencia activa
            </div>
          )}
          {(draft.alerts || []).map((a, i) => (
            <div key={i} className="flex items-center gap-1 text-[11px] text-amber-900"><AlertTriangle className="h-3 w-3" /> {a}</div>
          ))}
        </div>
      )}

      {isPending && (
        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-neutral-100">
          {editing ? (
            <>
              <button type="button" onClick={submitEdit} disabled={saving} className="btn-primary !h-8 !px-3 text-xs" data-testid={`draft-save-edit-${draft.id}`}>
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Guardar cambios
              </button>
              <button type="button" onClick={() => setEditing(false)} className="btn-ghost border border-neutral-200 !h-8 !px-3 text-xs">Cancelar</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => onAccept(draft)} disabled={saving} className="inline-flex items-center gap-1 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 text-xs shadow-sm disabled:opacity-50" data-testid={`draft-accept-${draft.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Aceptar (crear cita)
              </button>
              <button type="button" onClick={() => setEditing(true)} disabled={saving} className="btn-ghost border border-neutral-200 !h-8 !px-2 text-xs" data-testid={`draft-edit-${draft.id}`}>
                <Pencil className="h-3.5 w-3.5" /> Editar
              </button>
              <button type="button" onClick={() => onReject(draft)} disabled={saving} className="btn-ghost border border-red-200 text-red-600 hover:bg-red-50 !h-8 !px-2 text-xs" data-testid={`draft-reject-${draft.id}`}>
                <X className="h-3.5 w-3.5" /> Rechazar
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function PlannerDialog({ open, student, onClose, onAccepted }) {
  const [teachers, setTeachers] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessionId, setSessionId] = useState("");

  const sid = student?.id;

  const load = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try {
      const [t, d] = await Promise.all([
        api.get("/teachers", { params: { include_inactive: false } }),
        api.get("/planner/drafts", { params: { student_id: sid } }),
      ]);
      setTeachers(Array.isArray(t.data) ? t.data : []);
      setDrafts(Array.isArray(d.data) ? d.data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [sid]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const propose = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/planner/propose", { student_id: sid, num_proposals: 3 });
      setSessionId(data.session_id);
      // Merge fresh drafts on top
      const fresh = new Set(data.proposals.map((p) => p.id));
      setDrafts([...data.proposals, ...drafts.filter((d) => !fresh.has(d.id))]);
      toast.success(`3 propuestas generadas por la IA (session ${data.session_id.slice(-8)}).`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "El planificador IA no está disponible.");
    } finally {
      setGenerating(false);
    }
  };

  const accept = async (draft) => {
    if (!window.confirm(`¿Aceptar propuesta y crear la cita real del ${draft.date} a las ${draft.time}?`)) return;
    setSaving(true);
    try {
      await api.post(`/planner/drafts/${draft.id}/accept`);
      toast.success("Cita creada.");
      await load();
      if (typeof onAccepted === "function") onAccepted();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reject = async (draft) => {
    if (!window.confirm(`¿Rechazar propuesta del ${draft.date} a las ${draft.time}?`)) return;
    setSaving(true);
    try {
      await api.post(`/planner/drafts/${draft.id}/reject`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const editDraft = async (draftId, patch) => {
    setSaving(true);
    try {
      await api.patch(`/planner/drafts/${draftId}`, patch);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sortedDrafts = useMemo(() => {
    return [...drafts].sort((a, b) => {
      // Pending first, then by score desc, then by date+time
      if (a.status === "pending" && b.status !== "pending") return -1;
      if (b.status === "pending" && a.status !== "pending") return 1;
      if (b.score !== a.score) return (b.score || 0) - (a.score || 0);
      return `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`);
    });
  }, [drafts]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="planner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-orange-500" />
            Kai Planificador — {student?.name}
          </DialogTitle>
          <DialogDescription>
            Claude Sonnet 4.5 propone 3 clases prácticas óptimas. <b>La IA propone, tú decides:</b> puedes editar, aceptar o rechazar cada propuesta.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={propose}
            disabled={generating}
            className="inline-flex items-center gap-2 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-4 text-sm shadow-md disabled:opacity-50"
            data-testid="planner-generate"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {drafts.some((d) => d.status === "pending") ? "Generar 3 propuestas nuevas" : "Sugerir 3 propuestas"}
          </button>
          {sessionId && (
            <span className="text-[10px] text-neutral-400 font-mono">session: {sessionId.slice(-12)}</span>
          )}
          <button type="button" onClick={load} className="ml-auto btn-ghost border border-neutral-200 !h-9 !px-2 text-xs" data-testid="planner-refresh" title="Recargar">
            <RotateCw className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="space-y-3 pt-2" data-testid="planner-drafts">
          {loading ? (
            <div className="py-6 text-center text-sm text-neutral-500"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Cargando…</div>
          ) : sortedDrafts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center">
              <Sparkles className="h-6 w-6 text-orange-400 mx-auto mb-2" />
              <div className="text-sm font-semibold text-neutral-700">Sin propuestas todavía</div>
              <p className="text-xs text-neutral-500 mt-1">Pulsa "Sugerir 3 propuestas" para que la IA analice disponibilidad, incidencias y profesores.</p>
            </div>
          ) : (
            sortedDrafts.map((d) => (
              <DraftCard
                key={d.id}
                draft={d}
                teachers={teachers}
                onAccept={accept}
                onReject={reject}
                onEdit={editDraft}
                saving={saving}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
